import React from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Box, Divider, FormControlLabel, InputAdornment, Stack, Switch, TextField, Typography } from '@mui/material';

import { noWheelInputProps } from 'util/utils';
import { validateMenuItemName } from 'util/validation';
import { sizesComplete } from '../menuEditorPayloads';
import ColorField from './ColorField';
import SizesEditor from './SizesEditor';

/** Problems that block saving, keyed by field. */
export const dishFormErrors = (form, { isNew, printCategoryNames }) => {
  const errors = {};
  if (!form.name || !form.name.trim()) {
    errors.name = 'Name is required';
  } else if (validateMenuItemName(form.name)) {
    errors.name = validateMenuItemName(form.name);
  }
  if (validateMenuItemName(form.altName)) {
    errors.altName = validateMenuItemName(form.altName);
  }
  if (form.price === '' || form.price === null || Number.isNaN(Number(form.price))) {
    errors.price = 'Enter a price';
  } else if (Number(form.price) < 0) {
    errors.price = 'Cannot be negative';
  }
  if (form.minQuantity !== undefined && form.minQuantity !== null && form.minQuantity !== '' && Number(form.minQuantity) < 0) {
    errors.minQuantity = 'Cannot be negative';
  }
  if (!sizesComplete(form.sizes)) {
    errors.sizes = 'Every size needs a name and a price';
  } else if ((form.sizes || []).some((size) => Number(size.price) < 0)) {
    errors.sizes = 'Size prices cannot be negative';
  }
  if (isNew && printCategoryNames.length > 0 && form.printCategories.length === 0) {
    errors.printCategories = 'Pick at least one print category';
  }
  return errors;
};

const Section = ({ title, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1, letterSpacing: 0.8 }}>
      {title}
    </Typography>
    <Stack spacing={2}>{children}</Stack>
  </Box>
);

Section.propTypes = { title: PropTypes.string.isRequired, children: PropTypes.node };

const DishDetailsForm = ({ form, onChange, errors, isNew, printCategoryNames, currencySymbol, showErrors }) => {
  const set = (key) => (event) => onChange({ ...form, [key]: event.target.value });
  const toggle = (key) => (event) => onChange({ ...form, [key]: event.target.checked });
  const error = (key) => (showErrors || form[key] !== '') && errors[key];

  return (
    <Box>
      <Section title="Basics">
        <TextField label="Name" required fullWidth value={form.name} onChange={set('name')} error={!!error('name')} helperText={error('name') || ' '} autoFocus={isNew} />
        <TextField label="Alternative name" fullWidth value={form.altName} onChange={set('altName')} error={!!errors.altName} helperText={errors.altName || 'Shown alongside the name, e.g. in another language'} />
        <TextField label="Short name" fullWidth value={form.shortName} onChange={set('shortName')} helperText="Used where space is tight, like dockets" />
        <TextField label="Description" fullWidth multiline minRows={2} value={form.description} onChange={set('description')} />
      </Section>

      <Section title="Price">
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Price"
            required
            type="number"
            value={form.price}
            onChange={set('price')}
            error={!!error('price')}
            helperText={error('price') || ' '}
            InputProps={{ startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> }}
            inputProps={{ ...noWheelInputProps, min: 0 }}
            sx={{ flex: 1 }}
          />
          {!isNew && (
            <TextField
              label="Minimum quantity"
              type="number"
              value={form.minQuantity}
              onChange={set('minQuantity')}
              error={!!error('minQuantity')}
              helperText={error('minQuantity') || 'Least a customer can order'}
              inputProps={{ ...noWheelInputProps, min: 0 }}
              sx={{ flex: 1 }}
            />
          )}
        </Box>
        <FormControlLabel control={<Switch checked={form.gstFree} onChange={toggle('gstFree')} />} label="GST free" />
      </Section>

      <Divider sx={{ mb: 3 }} />
      <Box sx={{ mb: 3 }}>
        <SizesEditor sizes={form.sizes} onChange={(sizes) => onChange({ ...form, sizes })} currencySymbol={currencySymbol} />
        {showErrors && errors.sizes && (
          <Typography variant="caption" color="error">
            {errors.sizes}
          </Typography>
        )}
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Section title="Point of sale">
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label="Display number" value={form.displayNumber} onChange={set('displayNumber')} sx={{ flex: 1 }} />
          <TextField label="Product ID" value={form.productId} onChange={set('productId')} sx={{ flex: 1 }} />
        </Box>
        <TextField label="Ingredients" helperText="For the kitchen only" fullWidth multiline minRows={2} value={form.ingredients} onChange={set('ingredients')} />
        <Box>
          <FormControlLabel control={<Switch checked={form.isOpenTableItem} onChange={toggle('isOpenTableItem')} />} label="Open table item" />
          <FormControlLabel control={<Switch checked={form.isOpenTableSharedItem} onChange={toggle('isOpenTableSharedItem')} />} label="Open table shared item" />
        </Box>
        {isNew && printCategoryNames.length > 0 && (
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={printCategoryNames}
            value={form.printCategories}
            onChange={(event, value) => onChange({ ...form, printCategories: value })}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Print categories"
                required
                error={!!(showErrors && errors.printCategories)}
                helperText={(showErrors && errors.printCategories) || 'Where this dish prints in the kitchen'}
              />
            )}
          />
        )}
        {!isNew && (
          <ColorField
            label="Button colour"
            value={form.colorHex}
            onChange={(colorHex) => onChange({ ...form, colorHex })}
            helperText="Highlights the dish on the POS"
          />
        )}
      </Section>
    </Box>
  );
};

DishDetailsForm.propTypes = {
  form: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  isNew: PropTypes.bool,
  printCategoryNames: PropTypes.array.isRequired,
  currencySymbol: PropTypes.string.isRequired,
  showErrors: PropTypes.bool,
};

export default DishDetailsForm;
